/**
 * api/picklists.js — tenant picklist loading + admin CRUD.
 */
import { supabase } from "@/lib/supabaseClient";

/**
 * Loads tenant picklists into `{ category: [{value,label}] }`.
 *
 * Source: `lbs_picklists` (legacy `lbs_picklist_options` was dropped in 070).
 * Per-site scope is honoured via `scope_site_ids` (NULL/empty = all sites).
 */
const _picklistCache = new Map();
export function clearPicklistCache() { _picklistCache.clear(); }

export async function loadPicklists(siteId = null) {
  const key = siteId || "_all_";
  if (_picklistCache.has(key)) return _picklistCache.get(key);

  const modern = await supabase
    .from("lbs_picklists")
    .select("picklist_key,value,label,sort_order,scope_site_ids")
    .order("sort_order", { ascending: true });

  const inScope = (row) => {
    if (!siteId) return true;
    const scope = row.scope_site_ids;
    if (!scope || scope.length === 0) return true;
    return scope.includes(siteId);
  };

  const grouped = {};
  for (const row of modern.data || []) {
    if (!inScope(row)) continue;
    const k = row.picklist_key;
    grouped[k] ||= [];
    const idx = grouped[k].findIndex((o) => o.value === row.value);
    const entry = { value: row.value, label: row.label };
    if (idx >= 0) grouped[k][idx] = entry; else grouped[k].push(entry);
  }
  _picklistCache.set(key, grouped);
  return grouped;
}

/* Admin picklist CRUD --------------------------------------------------- */

export async function listPicklist(picklistKey, clientId = null) {
  let q = supabase.from("lbs_picklists").select("*").eq("picklist_key", picklistKey).eq("is_active", true).order("sort_order");
  if (clientId) q = q.or(`client_id.eq.${clientId},client_id.is.null`);
  else q = q.is("client_id", null);
  return q;
}

export async function listAllPicklists(clientId) {
  let q = supabase.from("lbs_picklists").select("*").order("picklist_key").order("sort_order");
  if (clientId) q = q.or(`client_id.eq.${clientId},client_id.is.null`);
  return q;
}

export const upsertPicklistItem = (row) =>
  row.id
    ? supabase.from("lbs_picklists").update(row).eq("id", row.id).select().maybeSingle()
    : supabase.from("lbs_picklists").insert(row).select().maybeSingle();

export const deletePicklistItem = (id) => supabase.from("lbs_picklists").delete().eq("id", id).select();
